<template>
  <div class="calendar-bulk-actions" v-if="isVisible">
    <button class="do-bttn calendar-bulk-actions__clear" @click="clearSelection">
      {{ i18n[locale]['cancel_selection'] }}
    </button>
    <DDropdown :isShowDots="false" :isBottomStart="false" :isCloseOnDropdownClick="true" :propOffset="[0, 8]">
      <template #title>
        <button id="vue-popper__massehandling_button"
                class="do-bttn calendar-bulk-actions__bttn">
          {{ i18n[locale]['bulk_actions'] }} ({{ selectedEventIds.length }})
          <span class="icon-arrow-down5"></span>
        </button>
      </template>
      <template #list>
        <ul>
          <li @click="startBulkCopy">
            <span class="icon-clipboard2"></span> {{ i18n[locale]['copy_events'] }}
          </li>
          <li @click="openDeleteModal('cancel')">
            <span class="icon-notification22"></span> {{ i18n[locale]['cancel_events'] }}
          </li>
          <li @click="openDeleteModal('uncancel')">
            <span class="icon-checkmark4"></span> {{ i18n[locale]['uncancel_events'] }}
          </li>
          <li @click="openDeleteModal('away')">
            <span class="icon-notification22"></span> {{ i18n[locale]['away_events'] }}
          </li>
          <li @click="openDeleteModal('unaway')">
            <span class="icon-checkmark4"></span> {{ i18n[locale]['unaway_events'] }}
          </li>
          <li class="d-item-warning" @click="openDeleteModal('week')">
            <span class="icon-bin"></span> {{ i18n[locale]['delete_in_week'] }}
          </li>
          <li class="d-item-warning" @click="openDeleteModal('onward')">
            <span class="icon-bin"></span> {{ i18n[locale]['delete_events_onward'] }}
          </li>
        </ul>
      </template>
    </DDropdown>

    <CopyEvent :bulk="true"></CopyEvent>

    <Modal v-if="isDeleteModal" @close="closeDeleteModal">
      <div slot="body" class="bulk-delete__modal text-dark">
        <div class="bulk-delete__modal__header">
          <h2>{{ i18n[locale]['are_you_sure'] }}</h2>
        </div>
        <div class="bulk-delete__modal__form">
          <h4>{{ deleteQuestion }}</h4>
          <div class="bulk-delete__modal__footer">
            <button @click.stop="closeDeleteModal" :disabled="isDeleting">
              {{ i18n[locale]['cancel'] }}
            </button>
            <button class="bttn--warning bttn marg-10-left"
                    :class="{'bttn--red': deleteMode !== 'uncancel' && deleteMode !== 'unaway'}"
                    @click="confirmDelete"
                    :disabled="isDeleting">
              <span v-if="isDeleting" class="bulk-delete__spinner"></span>
              <template v-else>{{ confirmButtonLabel }}</template>
            </button>
          </div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import DDropdown from '../common/DDropdown'
import CopyEvent from '../Events/CopyEvent'
import Modal from '../Subjects/Modal'
import docendo from '../../docendo'
import eventbus from '../../eventbus'

const I18N = {
  da: {
    bulk_actions: 'Massehandling',
    delete_events_onward: 'Slet lektioner fremadrettet',
    delete_in_week: 'Slet alle valgte',
    copy_events: 'Kopier lektioner',
    cancel_selection: 'Annuller',
    are_you_sure: 'Er du sikker?',
    delete_question_onward: 'Vil du slette de valgte lektioner, og alle der ligger på samme ugedag og i samme tidsrum fremadrettet?',
    delete_question_week: 'Vil du slette de valgte lektioner i denne uge?',
    cancel_events: 'Aflys valgte lektioner',
    uncancel_events: 'Fortryd aflysning',
    cancel_question: 'Vil du aflyse de valgte lektioner? De bliver ikke slettet, men markeret som aflyst.',
    uncancel_question: 'Vil du fortryde aflysningen af de valgte lektioner?',
    away_events: 'Markér som ude af huset',
    unaway_events: 'Fortryd ude af huset',
    away_question: 'Vil du markere de valgte lektioner som ude af huset? Personale på lektionerne vil fremgå i vikardækningen som fraværende.',
    unaway_question: 'Vil du fortryde markeringen som ude af huset for de valgte lektioner?',
    cancel: 'Annuller'
  },
  en: {
    bulk_actions: 'Bulk action',
    delete_events_onward: 'Delete lessons forward',
    delete_in_week: 'Delete all selected',
    copy_events: 'Copy lessons',
    cancel_selection: 'Cancel',
    are_you_sure: 'Are you sure?',
    delete_question_onward: 'Would you like to delete the selected lessons and all that lie on the same day of the week and in the same time period going forward?',
    delete_question_week: 'Would you like to delete the selected lessons in this week?',
    cancel_events: 'Cancel selected lessons',
    uncancel_events: 'Undo cancellation',
    cancel_question: 'Would you like to cancel the selected lessons? They will not be deleted but marked as canceled.',
    uncancel_question: 'Would you like to undo the cancellation of the selected lessons?',
    away_events: 'Mark as out of school',
    unaway_events: 'Undo out of school',
    away_question: 'Would you like to mark the selected lessons as out of school? Staff in the lessons will appear in the substitute management as absent.',
    unaway_question: 'Would you like to undo the out of school marking for the selected lessons?',
    cancel: 'Cancel'
  },
  nb: {
    bulk_actions: 'Massehandling',
    delete_events_onward: 'Slett timer fremover',
    delete_in_week: 'Slett alle valgte',
    copy_events: 'Kopier timer',
    cancel_selection: 'Avbryt',
    are_you_sure: 'Er du sikker?',
    delete_question_onward: 'Vil du slette de valgte timene og alle tilsvarende planlagte timer i samme tidsrom fremover i tid?',
    delete_question_week: 'Vil du slette de valgte timene i denne uken?',
    cancel_events: 'Avlys valgte timer',
    uncancel_events: 'Angre avlysning',
    cancel_question: 'Vil du avlyse de valgte timene? De blir ikke slettet, men markert som avlyst.',
    uncancel_question: 'Vil du angre avlysningen av de valgte timene?',
    away_events: 'Marker som utenfor skolen',
    unaway_events: 'Angre utenfor skolen',
    away_question: 'Vil du markere de valgte timene som utenfor skolen? Personalet på timene vil vises i vikarhåndteringen som fraværende.',
    unaway_question: 'Vil du angre markeringen som utenfor skolen for de valgte timene?',
    cancel: 'Avbryt'
  }
}

export default {
  components: {DDropdown, CopyEvent, Modal},
  data () {
    return {
      isDeleteModal: false,
      isDeleting: false,
      deleteMode: 'onward'
    }
  },
  computed: {
    locale () {
      return this.$store && this.$store.getters ? this.$store.getters.locale : 'da'
    },
    i18n () {
      return I18N
    },
    isMultiSelectActive () {
      return this.$store.getters.isMultiSelectActive
    },
    selectedEventIds () {
      return this.$store.state.calendar.selectedEventIds
    },
    selectedCalendarEvents () {
      return this.$store.getters.selectedCalendarEvents
    },
    calendarId () {
      return this.$store.state.calendar.calendarId
    },
    activeCalendarDate () {
      return this.$store.state.calendar.date
    },
    schoolId () {
      return this.$store.getters.schoolId
    },
    isVisible () {
      return this.selectedEventIds.length > 0
    },
    deleteQuestion () {
      const keys = {
        week: 'delete_question_week',
        onward: 'delete_question_onward',
        cancel: 'cancel_question',
        uncancel: 'uncancel_question',
        away: 'away_question',
        unaway: 'unaway_question'
      }
      return this.i18n[this.locale][keys[this.deleteMode]]
    },
    confirmButtonLabel () {
      const keys = {
        week: 'delete_in_week',
        onward: 'delete_events_onward',
        cancel: 'cancel_events',
        uncancel: 'uncancel_events',
        away: 'away_events',
        unaway: 'unaway_events'
      }
      return this.i18n[this.locale][keys[this.deleteMode]]
    }
  },
  methods: {
    clearSelection () {
      this.$store.commit('clearSelectedEventIds')
    },
    startBulkCopy () {
      const events = this.selectedCalendarEvents
      if (events.length < 1) {
        return
      }
      const first = events[0]
      // Backenden henter kilde-events i et 7-dages vindue der starter på source_date.
      // Brug derfor den tidligste valgte lektions dag, så alle valgte ugedage er dækket
      // (rækkefølgen i state.events er ikke tidssorteret).
      let earliestStart = first.start
      let earliestMs = first.start ? new Date(first.start).getTime() : null
      events.forEach(event => {
        if (!event.start) {
          return
        }
        const ms = new Date(event.start).getTime()
        if (earliestMs === null || ms < earliestMs) {
          earliestMs = ms
          earliestStart = event.start
        }
      })
      eventbus.copyEventsBulk = {
        event_ids: this.selectedEventIds.slice(),
        calendar_id: first.calendar_id ? first.calendar_id : this.calendarId * 1,
        school_id: first.school_id ? first.school_id : this.schoolId,
        start: earliestStart || this.activeCalendarDate,
        title: '',
        color: first.color ? first.color : '#468ad7',
        v_popper_anchor: '#vue-popper__massehandling_button'
      }
    },
    openDeleteModal (mode) {
      this.deleteMode = ['week', 'cancel', 'uncancel', 'away', 'unaway'].indexOf(mode) > -1 ? mode : 'onward'
      this.isDeleteModal = true
    },
    closeDeleteModal () {
      if (this.isDeleting) {
        return
      }
      this.isDeleteModal = false
    },
    confirmDelete () {
      if (this.deleteMode === 'week') {
        this.deleteSelectedEventsInWeek()
      } else if (this.deleteMode === 'cancel' || this.deleteMode === 'uncancel') {
        this.setSelectedEventsFlag('canceled', this.deleteMode === 'cancel')
      } else if (this.deleteMode === 'away' || this.deleteMode === 'unaway') {
        this.setSelectedEventsFlag('away', this.deleteMode === 'away')
      } else {
        this.deleteSelectedEventsOnward()
      }
    },
    setSelectedEventsFlag (prop, value) {
      const updatesBySchool = {}
      this.selectedCalendarEvents.forEach(event => {
        if (!!event[prop] === value) {
          return
        }
        const schoolId = event.school_id ? event.school_id : this.schoolId
        updatesBySchool[schoolId] = updatesBySchool[schoolId] || []
        updatesBySchool[schoolId].push({ event_id: event.event_id, [prop]: value })
      })
      const schoolIds = Object.keys(updatesBySchool)
      if (schoolIds.length < 1) {
        this.isDeleteModal = false
        this.$store.commit('clearSelectedEventIds')
        return
      }
      this.isDeleting = true
      let remaining = schoolIds.length
      const done = () => {
        remaining -= 1
        if (remaining === 0) {
          this.onAllDeleted()
        }
      }
      schoolIds.forEach(schoolId => {
        docendo.updateEvents(schoolId * 1, updatesBySchool[schoolId], done, failure => {
          console.log('bulk update ' + prop + ' failure', failure)
          done()
        })
      })
    },
    deleteSelectedEventsOnward () {
      const events = this.selectedCalendarEvents.slice()
      if (events.length < 1) {
        this.closeDeleteModal()
        return
      }
      this.isDeleting = true
      let remaining = events.length
      const done = () => {
        remaining -= 1
        if (remaining === 0) {
          this.onAllDeleted()
        }
      }
      events.forEach(event => {
        const schoolId = event.school_id ? event.school_id : this.schoolId
        docendo.eventsDeleteThisAndFollowing(schoolId, event.event_id, () => {
          this.$store.commit('deleteActiveCalendarEvent', event)
          done()
        }, failure => {
          console.log('bulk delete (onward) failure', failure)
          done()
        })
      })
    },
    deleteSelectedEventsInWeek () {
      const events = this.selectedCalendarEvents.slice()
      if (events.length < 1) {
        this.closeDeleteModal()
        return
      }
      this.isDeleting = true
      let remaining = events.length
      const done = () => {
        remaining -= 1
        if (remaining === 0) {
          this.onAllDeleted()
        }
      }
      events.forEach(event => {
        const schoolId = event.school_id ? event.school_id : this.schoolId
        docendo.deleteEvent(schoolId, event.event_id, () => {
          this.$store.commit('deleteActiveCalendarEvent', event)
          done()
        }, failure => {
          console.log('bulk delete (week) failure', failure)
          done()
        })
      })
    },
    onAllDeleted () {
      this.isDeleting = false
      this.isDeleteModal = false
      this.$store.commit('clearSelectedEventIds')
      this.$store.dispatch('readCalendarSubjects')
    }
  }
}
</script>

<style scoped>
.calendar-bulk-actions {
  display: inline-flex;
  align-items: stretch;
  gap: 0.5rem;
}

.calendar-bulk-actions__bttn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: bold;
  font-size: 12px;
}

.calendar-bulk-actions__clear {
  font-size: 12px;
}

.bulk-delete__modal__header {
  position: relative;
  padding: 30px 20px;
  background-color: #989898;
  border-radius: 4px 4px 0 0;
}

.bulk-delete__modal__header > h2 {
  color: white;
  margin-top: 0;
}

.bulk-delete__modal__form {
  position: relative;
  padding: 1rem 2rem;
}

.bulk-delete__modal__footer {
  display: flex;
  justify-content: flex-end;
  padding: 30px 20px 10px;
}

.bulk-delete__spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #ffffff;
  animation: bulkDeleteSpin 0.6s linear infinite;
  vertical-align: middle;
}

@keyframes bulkDeleteSpin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>



// WEBPACK FOOTER //
// CalendarBulkActions.vue?66198f32